<template>
  <section class="sessions-content">
    <GridBlockBase id="sessions-listing" :title="t('ui.layout.nav.sessions')">
      <!--───────────── Icon (header) ─────────────-->
      <template #icon>
        <calendarIcon color="black" filled />
      </template>

      <!--───────────── Body ─────────────-->
      <template #body>
        <SessionsList :sessions="filteredSessionsRef" :isPending="isPendingRef" order="desc" />
      </template>

      <!--───────────── Header extra (filters + actions) ─────────────-->
      <template #headerExtra>
        <!-- Desktop filters -->
        <div class="desktop-session-filters">
          <CustomSelect
            id="sessions-status"
            :value="selectedStatusRef"
            :options="sessionsListOptionsRef"
            :label="t('ui.filter.status')"
            hide-label
            @emit:change="onChangeStatus"
          />
          <CustomSelect
            id="sessions-group"
            :value="tempSelectedGroupIdRef || 'ALL'"
            :options="groupsOptionsRef"
            :label="t('ui.filter.group')"
            hide-label
            @emit:change="onChangeGroup"
          />
        </div>

        <!-- Mobile button that opens the popover -->
        <div
          class="btn-filters"
          role="button"
          tabindex="0"
          :aria-expanded="popoverIsOpenRef"
          @click.stop="openPopoverOnClick"
          @keyup.enter.prevent="openPopoverOnKey"
        >
          <FilterIcon color="black" filled />
          {{ t('ui.button.filters') }}
        </div>

        <!-- Popover (mobile filters) -->
        <Popover
          :popover="{
            name: 'programs',
            isOpen: popoverIsOpenRef,
            event: popoverEventRef || undefined,
          }"
        >
          <template #content>
            <CustomSelect
              id="sessions-status-mobile"
              :value="selectedStatusRef"
              :options="sessionsListOptionsRef"
              :label="t('ui.filter.status')"
              hide-label
              placement="top"
              @emit:change="onChangeStatus"
            />
            <CustomSelect
              id="sessions-group-mobile"
              :value="tempSelectedGroupIdRef || 'ALL'"
              :options="groupsOptionsRef"
              :label="t('ui.filter.group')"
              hide-label
              placement="top"
              @emit:change="onChangeGroup"
            />
          </template>
        </Popover>

        <!-- New session button -->
        <CustomButton
          v-if="
            programRolesRef?.includes(RoleEnumTypeV2.MENTOR) ||
            (programRolesRef?.includes(RoleEnumTypeV2.MENTEE) && menteeCanBookSessionRef)
          "
          testId="create_session-button-schedule-session"
          class="sessions-content__new-session-button"
          color="primary"
          usage="button"
          @emit:click="openNewSessionModal"
        >
          <template #icon>
            <span class="sessions-content__new-session-button__text">{{ t('ui.layout.nav.newSession') }}</span>
            <AddCalendarIcon color="black" filled />
          </template>
        </CustomButton>
      </template>

      <!--───────────── Footer (pagination) ─────────────-->
      <template #footer>
        <div>
          <span v-if="isPendingRef" class="table-sub-info">{{ t('ui.message.loading') }}</span>
          <span v-else class="table-sub-info">
            {{
              t('ui.pagination.resultsOfOn', {
                start: Math.min(Math.max(1, perPageRef * (pageRef - 1) + 1), totalSessionsRef || 1),
                end: Math.min(perPageRef * pageRef, totalSessionsRef || 1),
                total: totalSessionsRef,
              })
            }}
          </span>
        </div>

        <!-- Desktop pagination (select) -->
        <div class="page-select-wrapper">
          <CustomSelect
            placement="top"
            id="page-select"
            :value="String(pageRef)"
            :options="pagesOptionsRef"
            :label="t('ui.pagination.page')"
            hide-label
            @emit:change="onPageChange"
          />
          <span v-if="isPendingRef" class="table-sub-info table-sub-info__total-pages">{{
            t('ui.message.loading')
          }}</span>
          <span v-else class="table-sub-info table-sub-info__total-pages">{{
            t('ui.pagination.totalPages', { total: totalPagesRef })
          }}</span>
        </div>

        <!-- Mobile pagination (< 1 2 >) -->
        <MobilePagination :page="pageRef" :total="totalPagesRef ?? 1" @change="onPageChange" />
      </template>
    </GridBlockBase>
  </section>
</template>

<script setup lang="ts">
// ───────────────────────── Imports ─────────────────────────
import { computed, onBeforeMount, ref, watch } from 'vue'

import { RoleEnumTypeV2 } from '@/features/auth/types'
import useGroups from '@/features/groups/hooks/use-groups.hook'
import { selectGetTempSelectedGroupId } from '@/features/groups/stores/groups/groups.selectors'
import groupsStore from '@/features/groups/stores/groups/groups.store'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import SessionsList from '@/features/sessions/components/sessions-list/sessions-list.vue'
import useSessions from '@/features/sessions/hooks/use-sessions.hook'
import { SessionStatusEnumType } from '@/features/sessions/types'
import type { SessionsStatusesOptionsType } from '@/features/sessions/utils'
import {
  getCanceledSessions,
  getCompletedSessions,
  getPlannedSessions,
  getWaitConfirmationSessions,
  getWaitReviewSessions,
  SessionOptionEnumDefaultAllType,
  sessionStatusesOptionsMap,
} from '@/features/sessions/utils'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import GridBlockBase from '@/features/ui/components/grid-block-base/grid-block-base.vue'
import AddCalendarIcon from '@/features/ui/components/icons/add-calendar-icon.vue'
import calendarIcon from '@/features/ui/components/icons/calendar-icon.vue'
import FilterIcon from '@/features/ui/components/icons/filter-icon.vue'
import MobilePagination from '@/features/ui/components/pagination/mobile-pagination.vue'
import Popover from '@/features/ui/components/popover/popover.vue'
import CustomSelect from '@/features/ui/components/select/custom-select.vue'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import usePopoverTrigger from '@/hooks/use-popover-trigger.hook'
import { useI18n } from '@/lib/i18n'

// ───────────────────────── Popover logic ─────────────────────────
const {
  isOpenRef: popoverIsOpenRef,
  eventRef: popoverEventRef,
  openOnClick: openPopoverOnClick,
  openOnKey: openPopoverOnKey,
} = usePopoverTrigger()

// ───────────────────────── i18n ─────────────────────────
const { t } = useI18n()

// ───────────────────────── Pagination state ─────────────────────────
const pageRef = ref(1)
const perPageRef = ref(20)

// Pages list for desktop select
const pagesOptionsRef = computed(() =>
  Array.from({ length: totalPagesRef.value || 0 }, (_, i) => ({
    label: `${t('ui.pagination.page')} ${i + 1}`,
    value: `${i + 1}`,
  })),
)

// ───────────────────────── Data fetching ─────────────────────────
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const { programRef } = useProgram(selectedProgramIdRef)
const menteeCanBookSessionRef = computed(() => programRef?.value?.settings.menteeCanBookSession)

const { sessionsRef, isPendingRef, totalSessionsRef, totalPagesRef } = useSessions({ pageRef, perPageRef })
const { programRolesRef } = useProgramRoles()

// Reset metadata on program change
watch(selectedProgramIdRef, () => {
  pageRef.value = 1
  groupsStore.setTempSelectedGroupId(null)
})

onBeforeMount(() => groupsStore.setTempSelectedGroupId(null))

function onPageChange(v: number | string) {
  pageRef.value = Number(v)
}

// ───────────────────────── Groups select ─────────────────────────
const { groupsRef } = useGroups({ perPageRef: ref(9999) })
const tempSelectedGroupIdRef = selectGetTempSelectedGroupId(groupsStore)

const groupsOptionsRef = computed(() => [
  { label: t('ui.layout.nav.allGroups'), value: 'ALL' },
  ...groupsRef.value
    .slice()
    .sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0))
    .map((g) => ({ value: g.id, label: g.name })),
])

function onChangeGroup(value: string) {
  value === 'ALL' ? groupsStore.setTempSelectedGroupId(null) : groupsStore.setTempSelectedGroupId(value)
}

// ───────────────────────── Status filter ─────────────────────────
const plannedSessionsRef = computed(() => sessionsRef.value.filter(getPlannedSessions))
const waitConfirmationSessionsRef = computed(() => sessionsRef.value.filter(getWaitConfirmationSessions))
const waitReviewSessionsRef = computed(() => sessionsRef.value.filter(getWaitReviewSessions))
const completedSessionsRef = computed(() => sessionsRef.value.filter(getCompletedSessions))
const canceledSessionsRef = computed(() => sessionsRef.value.filter(getCanceledSessions))

const sessionsListOptionsRef = computed(() => [
  {
    label: `${t(sessionStatusesOptionsMap[SessionOptionEnumDefaultAllType.ALL])} · ${sessionsRef.value.length}`,
    value: SessionOptionEnumDefaultAllType.ALL,
  },
  {
    label: `${t(sessionStatusesOptionsMap[SessionStatusEnumType.PLANNED])} · ${plannedSessionsRef.value.length}`,
    value: SessionStatusEnumType.PLANNED,
  },
  {
    label: `${t(sessionStatusesOptionsMap[SessionStatusEnumType.WAIT_CONFIRMATION])} · ${waitConfirmationSessionsRef.value.length}`,
    value: SessionStatusEnumType.WAIT_CONFIRMATION,
  },
  {
    label: `${t(sessionStatusesOptionsMap[SessionStatusEnumType.WAIT_REVIEW])} · ${waitReviewSessionsRef.value.length}`,
    value: SessionStatusEnumType.WAIT_REVIEW,
  },
  {
    label: `${t(sessionStatusesOptionsMap[SessionStatusEnumType.FINISHED])} · ${completedSessionsRef.value.length}`,
    value: SessionStatusEnumType.FINISHED,
  },
  {
    label: `${t(sessionStatusesOptionsMap[SessionStatusEnumType.CANCELLED])} · ${canceledSessionsRef.value.length}`,
    value: SessionStatusEnumType.CANCELLED,
  },
])

const selectedStatusRef = ref<SessionsStatusesOptionsType>(SessionOptionEnumDefaultAllType.ALL)

function onChangeStatus(v: SessionsStatusesOptionsType) {
  selectedStatusRef.value = v
}

const filteredSessionsRef = computed(() =>
  selectedStatusRef.value === SessionOptionEnumDefaultAllType.ALL
    ? sessionsRef.value
    : sessionsRef.value.filter((s) => s.status === selectedStatusRef.value),
)

// ───────────────────────── New session modal ─────────────────────────
function openNewSessionModal() {
  modalsStore.toggleModal('new-session', true)
}
</script>

<style lang="scss">
@import './sessions-content.scss';

.sessions-content {
  .desktop-session-filters {
    display: flex;
    gap: $s;
  }

  .btn-filters {
    display: none;
    align-items: center;
    gap: $s;
    cursor: pointer;
    padding: 0.75rem 1.5rem;
    border: 1px solid rgba(var(--rgb-black), 0.05);
    border-radius: 0.5rem;
  }

  .page-select-wrapper {
    display: flex;
    align-items: center;
    gap: $s;
  }

  .mobile-pagination {
    display: none;
    align-items: center;
    gap: $s;

    .page-btn,
    .nav-btn {
      border: 1px solid rgba(var(--rgb-black), 0.1);
      background: transparent;
      padding: $s / 2 $s;
      border-radius: 0.25rem;
      font-size: 0.875rem;
      &.active {
        background: rgba(var(--rgb-black), 0.05);
        font-weight: 600;
      }
      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
    }
  }

  @include mq('<desktop') {
    .desktop-session-filters,
    .sessions-content__new-session-button__text,
    .page-select-wrapper {
      display: none;
    }
    .btn-filters {
      display: flex;
    }
    .mobile-pagination {
      display: flex;
    }
    #page-select {
      display: none;
    }
    .table-sub-info__total-pages {
      display: none;
    }
  }
}
</style>
